import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useMemo, type ReactElement } from 'react';
import { Select, Tooltip } from 'antd';

import {
  DEFAULT_OPENROUTER_CHAT_MODEL,
  isOpenRouterChatModel,
  OPENROUTER_CHAT_MODELS,
} from '@govard/llm-gate-client';
import type { OpenRouterChatModelId } from '@govard/llm-gate-client';
import type { LlmGatewayProfile } from '../api/chat.client.js';
import { isAvailableChatGateway } from '../lib/available-chat-gateways.js';

export interface ModelGatewaySelectorProps {
  gateway: LlmGatewayProfile;
  openRouterModel?: OpenRouterChatModelId;
  disabled?: boolean;
  onGatewayChange: (gateway: LlmGatewayProfile) => void;
  onOpenRouterModelChange?: (model: OpenRouterChatModelId) => void;
}

type SelectValue = 'openrouter' | 'opencode' | `openrouter:${OpenRouterChatModelId}`;

export function ModelGatewaySelector({
  gateway,
  openRouterModel = DEFAULT_OPENROUTER_CHAT_MODEL,
  disabled = false,
  onGatewayChange,
  onOpenRouterModelChange,
}: ModelGatewaySelectorProps): ReactElement {
  useI18nSubscription();

  const value: SelectValue | null = isAvailableChatGateway(gateway)
    ? gateway === 'openrouter'
      ? `openrouter:${openRouterModel}`
      : gateway
    : null;

  const options = useMemo(
    () => [
      {
        label: i18n.t('crm.ai_chat.model_selector.provider'),
        options: [
          { value: 'openrouter', label: i18n.t('crm.ai_chat.model_selector.openrouter') },
          { value: 'opencode', label: i18n.t('crm.ai_chat.model_selector.opencode') },
        ],
      },
      {
        label: i18n.t('crm.ai_chat.model_selector.model'),
        options: OPENROUTER_CHAT_MODELS.map((model) => ({
          value: `openrouter:${model.id}` as SelectValue,
          label: model.label,
          badge: i18n.t(`crm.ai_chat.model_selector.badge.${model.id}`),
        })),
      },
    ],
    [i18n.locale],
  );

  const handleChange = (next: SelectValue): void => {
    if (next === 'opencode') {
      onGatewayChange('opencode');
      return;
    }
    if (next === 'openrouter') {
      onGatewayChange('openrouter');
      onOpenRouterModelChange?.(openRouterModel);
      return;
    }
    const model = next.slice('openrouter:'.length);
    if (!isOpenRouterChatModel(model)) {
      return;
    }
    onGatewayChange('openrouter');
    if (onOpenRouterModelChange !== undefined) {
      onOpenRouterModelChange(model);
    }
  };

  return (
    <Tooltip
      fresh
      title={i18n.t(
        'crm.features.features.ai.chat.components.gatewayprofileselector.marshrut_modeli_zakreplen_za_tekushchim_dialog',
      )}
    >
      <Select<SelectValue>
        aria-label={i18n.t('crm.ai_chat.model_selector.label')}
        size="small"
        value={value}
        placeholder={i18n.t('crm.ai_chat.model_selector.choose_available')}
        disabled={disabled}
        className="min-w-44"
        popupMatchSelectWidth={false}
        optionLabelProp="label"
        onChange={handleChange}
        options={options}
        optionRender={(option) => {
          const badge = (option.data as { badge?: string }).badge;
          return badge === undefined ? (
            option.label
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span>{option.label}</span>
              <EntityStatusChip tone="default" className="m-0 text-[10px]">
                {badge}
              </EntityStatusChip>
            </div>
          );
        }}
      />
    </Tooltip>
  );
}
